import { BaseModel } from 'model/BaseModel';
import { Button } from 'primereact/button';
import { useLoadingContext } from 'provider/LoadingProvider';
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BaseServiceCadastros } from 'service/BaseServiceCadastros';
import Base from './Base';
import { useToastContext } from './alert/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';

type Props = {
    children?: ReactNode;
    botoesExtra?: ReactNode;
    model: BaseModel;
    setModel(model: BaseModel): void;
    service: BaseServiceCadastros<BaseModel>;
    validar(): boolean;
    getNewModel?: () => BaseModel;
    validarPersonalizado?: () => boolean;
    getModel?: () => BaseModel | FormData;
    pathCadastro: string;
    urlService: string;
    tituloPagina: string;
    showGravarButton: boolean;
    onSave?(model: BaseModel): void;
    afterLoad(model: BaseModel): void;
    isModal?: boolean;
    novoCadastro?: boolean;
    duplicar?: boolean;
    fieldsToExcludeOnDuplicate?: string[];
    setModelEditando?(modelEditando: boolean): void;
    id?: number;
};

const DefaultBaseCadastro = (props: Props) => {

    const [queryParameters] = useSearchParams();

    const { msgSucesso, msgErro, msgAlerta } = useToastContext();
    const { startLoading, stopLoading } = useLoadingContext();

    const navigate = useNavigate();
    const [editando, setEditando] = useState<boolean>(false);
    const [duplicando, setDuplicando] = useState<{ duplicando: boolean, completeModel: BaseModel }>({ duplicando: false, completeModel: { id: 0, codigo: "" } });
    const [idConta] = useState<number | null>(queryParameters.has('idConta') ? parseInt(queryParameters.get('idConta') || '0') : null);

    useEffect(() => {
        if (props.isModal) {
            if (props.id) {
                setEditando(true);
                consultar(props.id);
            }
        } else if (window.location.pathname !== (props.pathCadastro + '/cadastrar')) {
            setEditando(true);
            consultar(window.location.pathname.substring(`${props.pathCadastro}/editar/`.length));
        }
    }, []);

    function consultar(id: number | string) {
        props.service.consultar(
            id,
            idConta ? idConta : undefined
        ).then((modelParam: BaseModel) => {
            props.setModel(modelParam);
            props.afterLoad(modelParam);
        }).catch(error => {
            console.error(error);
            if (error && error.data && error.data.message) {
                msgErro(props.tituloPagina, error.data.message);
            } else {
                msgErro(props.tituloPagina, 'Ocorreu um erro ao tentar carregar, tente novamente!');
            }
            if (error.status === 404 && !props.isModal) {
                navigate(-1);
            }
        });
    }

    useEffect(() => {
        if (window.location.pathname === (props.pathCadastro + '/cadastrar')) {
            if (props.getNewModel) {
                setEditando(false);
                props.setModel(props.getNewModel());
                props.afterLoad(props.getNewModel());
            }
        }
        if (duplicando.duplicando) {
            const fieldsToExclude = ['id', 'codigo', ...(props.fieldsToExcludeOnDuplicate || [])];
            let newModel: BaseModel;

            if (props.getNewModel) {
                newModel = props.getNewModel();
                Object.keys(props.model).forEach(key => {
                    if (!fieldsToExclude.includes(key)) {
                        (newModel as any)[key] = (props.model as any)[key];
                    }
                });
                props.setModel(newModel);
                props.afterLoad(newModel);
            }
            setDuplicando({ duplicando: false, completeModel: { id: 0, codigo: "" } });
        }
    }, [window.location.pathname])

    function voltar() {

        navigate(-1);
    }

    function gravar() {

        if (props.validar()) {

            if (!props.validarPersonalizado || props.validarPersonalizado()) {

                startLoading();
                if (editando) {
                    props.service
                        .editar(props.getModel ? props.getModel() : props.model, idConta)
                        .then((modelRetorno: BaseModel) => {
                            stopLoading();
                            consultarModeloGravado(modelRetorno);
                            msgSucesso(props.tituloPagina, 'Registro gravado com sucesso');
                        })
                        .catch(error => {
                            erroGravar(error);
                        });
                } else {
                    props.service
                        .incluir(props.getModel ? props.getModel() : props.model, idConta)
                        .then((modelRetorno: BaseModel) => {
                            stopLoading();
                            msgSucesso(props.tituloPagina, 'Registro gravado com sucesso');

                            consultarModeloGravado(modelRetorno);

                            setEditando(true);

                            if (!props.isModal) {

                                let ident = modelRetorno.id ? modelRetorno.id : modelRetorno.codigo;
                                let url = `${props.pathCadastro}/editar/${ident}`;
                                if (idConta != null) {
                                    url += '?idConta=' + idConta;
                                }

                                navigate(url, { replace: true });
                            }
                        })
                        .catch(error => {
                            erroGravar(error);
                        });
                }

            }

        } else {
            msgAlerta(props.tituloPagina, 'Favor, preencher todos os campos obrigatórios');
        }

    }

    function duplicar() {
        props.setModelEditando && props.setModelEditando(false);
        setDuplicando({
            ...duplicando,
            duplicando: true,
            completeModel: props.model
        })
        let url = `${props.pathCadastro}/cadastrar`;
        if (idConta != null) {
            url += '?idConta=' + idConta;
        }
        navigate(url, { replace: true });
    }

    function novoCadastro() {
        let url = `${props.pathCadastro}/cadastrar`;
        if (idConta != null) {
            url += '?idConta=' + idConta;
        }
        navigate(url, { replace: true });
    }

    function consultarModeloGravado(model: BaseModel) {
        let ident = model.id ? model.id : model.codigo;
        props.service.consultar(ident || 0, idConta).then((modelParam: BaseModel) => {
            props.setModel(modelParam);
            props.onSave && props.onSave(modelParam);
        }).catch(error => {
            console.error(error);
            if (error && error.data && error.data.message) {
                msgErro(props.tituloPagina, error.data.message);
            } else {
                msgErro(props.tituloPagina, 'Ocorreu um erro ao tentar gravar, tente novamente!');
            }
        });
    }

    function erroGravar(error: any) {
        stopLoading();
        console.error(error);
        if (error && error.data && error.data.message) {
            msgErro(props.tituloPagina, error.data.message);
        } else {
            msgErro(props.tituloPagina, 'Ocorreu um erro ao tentar gravar, tente novamente!');
        }
    }

    return (
        <Base tituloPagina={props.tituloPagina}>
            <div className="container">
                {props.children}

                <div className="row">
                    <div className="col-sm-12" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {!props.isModal ? <Button type="button" label="Voltar" className="ui-button-info" onClick={() => voltar()}></Button> : null}
                        {editando && props.getNewModel && props.novoCadastro && !props.isModal && < Button type="button" label="Novo cadastro" icon={<FontAwesomeIcon icon="plus" />} style={{ marginLeft: 8 }} onClick={() => novoCadastro()}></Button>}
                        {editando && props.getNewModel && props.duplicar && !props.isModal && < Button type="button" label="Duplicar" icon={<FontAwesomeIcon icon="plus" />} style={{ marginLeft: 8 }} onClick={() => duplicar()}></Button>}
                        {props.botoesExtra}
                        {props.showGravarButton && <Button type="button" label="Gravar" className="ui-button-info" style={{ marginLeft: 8 }} onClick={() => gravar()}></Button>}
                    </div>
                </div>
            </div>
        </Base >
    );
};

function BaseCadastro(props: Props) {

    return (
        <DefaultBaseCadastro
            service={props.service} validar={props.validar} validarPersonalizado={props.validarPersonalizado}
            model={props.model} setModel={props.setModel} getModel={props.getModel} getNewModel={props.getNewModel}
            pathCadastro={props.pathCadastro} urlService={props.urlService} tituloPagina={props.tituloPagina} botoesExtra={props.botoesExtra}
            afterLoad={props.afterLoad} onSave={props.onSave} showGravarButton={props.showGravarButton} isModal={props.isModal} id={props.id}
            novoCadastro={props.novoCadastro} duplicar={props.duplicar} fieldsToExcludeOnDuplicate={props.fieldsToExcludeOnDuplicate} setModelEditando={props.setModelEditando}
        >
            {props.children}
        </DefaultBaseCadastro>
    );
}

BaseCadastro.defaultProps = {
    showGravarButton: true,
    afterLoad: () => { }
};

export default BaseCadastro;
